import jsPDF from 'jspdf';
import 'jspdf-autotable';

/**
 * Auto-detect ideal column widths based on dataKey/header content.
 * Returns a columnStyles object compatible with jspdf-autotable.
 */
const buildColumnStyles = (columns, pageWidth) => {
  // Reserve weight per column type (relative units, normalized later)
  const styles = {};
  let totalWeight = 0;
  const weights = columns.map((col, idx) => {
    const key = (col.dataKey || '').toLowerCase();
    const hdr = (col.header || '').toLowerCase();
    let w = 1.0;  // default
    if (key.includes('description') || hdr.includes('description') || key.includes('remarks') || key.includes('notes')) w = 3.5;
    else if (key.includes('date') || hdr.includes('date')) w = 1.0;
    else if (key.includes('amount') || hdr.includes('amount') || hdr.includes('rs') || hdr.includes('total')) w = 1.1;
    else if (key.includes('category') || key.includes('mode') || key.includes('status')) w = 1.1;
    else if (key.includes('title') || key.includes('name')) w = 1.5;
    else if (key.includes('id') || key.includes('number') || key.includes('phone')) w = 1.2;
    totalWeight += w;
    return w;
  });
  // Available content width (page width - margins) — margins are 14 each side
  const margin = 14;
  const usableWidth = pageWidth - 2 * margin;
  weights.forEach((w, idx) => {
    const cellWidth = (w / totalWeight) * usableWidth;
    const key = (columns[idx].dataKey || '').toLowerCase();
    const isAmount = key.includes('amount');
    styles[columns[idx].dataKey] = {
      cellWidth,
      halign: isAmount ? 'right' : 'left',
    };
  });
  return styles;
};

/**
 * Render branded header (college name + address + phone + divider).
 * Returns the new yPos.
 */
const renderHeader = (doc, collegeInfo, pageWidth) => {
  const collegeName = collegeInfo?.college_name || collegeInfo?.name || '';
  const collegeAddress = collegeInfo?.address || '';
  const collegePhone = collegeInfo?.phone || '';
  let yPos = 12;
  if (collegeName) {
    doc.setFontSize(15);
    doc.setTextColor(0);
    doc.setFont(undefined, 'bold');
    doc.text(collegeName.toUpperCase(), pageWidth / 2, yPos, { align: 'center' });
    yPos += 6;
    doc.setFontSize(8.5);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(80);
    if (collegeAddress) {
      doc.text(collegeAddress, pageWidth / 2, yPos, { align: 'center' });
      yPos += 4.5;
    }
    if (collegePhone) {
      doc.text(`Phone: ${collegePhone}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 4.5;
    }
    yPos += 1.5;
    doc.setDrawColor(30, 64, 175);
    doc.setLineWidth(0.5);
    doc.line(14, yPos, pageWidth - 14, yPos);
    yPos += 5;
  }
  return yPos;
};

/**
 * Export table data to PDF
 * @param {string} title - Report title
 * @param {Array} columns - Array of column objects {header: string, dataKey: string}
 * @param {Array} data - Array of data objects
 * @param {string} filename - Output filename
 * @param {Object} collegeInfo - Tenant information for branding
 */
export const exportTableToPDF = (title, columns, data, filename = 'report.pdf', collegeInfo = null) => {
  // Use landscape for tables with 5+ columns or any description-type field for better readability
  const useLandscape = columns.length >= 5 || columns.some(c =>
    /description|remarks|notes/i.test(c.dataKey || c.header || '')
  );
  const doc = new jsPDF({ orientation: useLandscape ? 'landscape' : 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.width;

  let yPos = renderHeader(doc, collegeInfo, pageWidth);

  // Title
  doc.setFontSize(13);
  doc.setTextColor(30, 64, 175);
  doc.setFont(undefined, 'bold');
  doc.text(title, 14, yPos);
  yPos += 5;

  // Metadata
  doc.setFontSize(8.5);
  doc.setTextColor(100);
  doc.setFont(undefined, 'normal');
  doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, yPos);
  yPos += 6;

  const columnStyles = buildColumnStyles(columns, pageWidth);

  doc.autoTable({
    columns,
    body: data,
    startY: yPos,
    theme: 'grid',
    styles: {
      fontSize: 8,
      cellPadding: 2,
      overflow: 'linebreak',
      valign: 'middle',
      lineColor: [220, 220, 220],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [30, 64, 175],
      textColor: 255,
      fontSize: 8.5,
      fontStyle: 'bold',
      halign: 'center',
      cellPadding: 2.5,
    },
    bodyStyles: { fontSize: 8, cellPadding: 2 },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    columnStyles,
    margin: { left: 14, right: 14 },
    didDrawPage: function (data) {
      const pageCount = doc.internal.getNumberOfPages();
      doc.setFontSize(8);
      doc.setTextColor(100);
      doc.text(
        `Page ${data.pageNumber} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.height - 7,
        { align: 'center' }
      );
    },
  });

  doc.save(filename);
};

/**
 * Export summary report with stats to PDF
 * @param {string} title - Report title
 * @param {Object} stats - Statistics object
 * @param {Array} columns - Table columns
 * @param {Array} data - Table data
 * @param {string} filename - Output filename
 * @param {Object} collegeInfo - Tenant information for branding
 */
export const exportSummaryReportToPDF = (title, stats, columns, data, filename = 'report.pdf', collegeInfo = null) => {
  const useLandscape = columns.length >= 5 || columns.some(c =>
    /description|remarks|notes/i.test(c.dataKey || c.header || '')
  );
  const doc = new jsPDF({ orientation: useLandscape ? 'landscape' : 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.width;

  let yPos = renderHeader(doc, collegeInfo, pageWidth);

  // Report Title
  doc.setFontSize(13);
  doc.setTextColor(30, 64, 175);
  doc.setFont(undefined, 'bold');
  doc.text(title, 14, yPos);
  yPos += 5;

  // Metadata
  doc.setFontSize(8.5);
  doc.setTextColor(100);
  doc.setFont(undefined, 'normal');
  doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, yPos);
  yPos += 6;

  // Statistics Section — render as 2-column grid for better space usage
  doc.setFontSize(10.5);
  doc.setTextColor(0);
  doc.setFont(undefined, 'bold');
  doc.text('Summary', 14, yPos);
  yPos += 5;

  doc.setFontSize(8.5);
  doc.setFont(undefined, 'normal');
  const statEntries = Object.entries(stats);
  const colGap = pageWidth / 2;
  statEntries.forEach((entry, idx) => {
    const [key, value] = entry;
    const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    const col = idx % 2;
    const xPos = 14 + col * colGap;
    doc.setFont(undefined, 'bold');
    doc.setTextColor(60);
    doc.text(`${label}:`, xPos, yPos + Math.floor(idx / 2) * 5);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0);
    doc.text(String(value), xPos + 35, yPos + Math.floor(idx / 2) * 5);
  });
  yPos += Math.ceil(statEntries.length / 2) * 5 + 4;

  const columnStyles = buildColumnStyles(columns, pageWidth);

  doc.autoTable({
    columns,
    body: data,
    startY: yPos,
    theme: 'grid',
    styles: {
      fontSize: 8,
      cellPadding: 2,
      overflow: 'linebreak',
      valign: 'middle',
      lineColor: [220, 220, 220],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [30, 64, 175],
      textColor: 255,
      fontSize: 8.5,
      fontStyle: 'bold',
      halign: 'center',
      cellPadding: 2.5,
    },
    bodyStyles: { fontSize: 8, cellPadding: 2 },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    columnStyles,
    margin: { left: 14, right: 14 },
    didDrawPage: function (data) {
      const pageCount = doc.internal.getNumberOfPages();
      doc.setFontSize(8);
      doc.setTextColor(100);
      doc.text(
        `Page ${data.pageNumber} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.height - 7,
        { align: 'center' }
      );
    },
  });

  doc.save(filename);
};

/**
 * Export simple list report to PDF
 * @param {string} title - Report title
 * @param {Array} items - Array of items to display
 * @param {string} filename - Output filename
 */
export const exportListToPDF = (title, items, filename = 'report.pdf') => {
  const doc = new jsPDF();
  
  doc.setFontSize(18);
  doc.setTextColor(30, 64, 175);
  doc.text(title, 14, 20);
  
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 28);
  
  let yPos = 40;
  doc.setFontSize(10);
  doc.setTextColor(0);
  
  items.forEach((item, index) => {
    if (yPos > 280) {
      doc.addPage();
      yPos = 20;
    }
    doc.text(`${index + 1}. ${item}`, 14, yPos);
    yPos += 6;
  });
  
  doc.save(filename);
};
